/**
 * DocumentUpload — true file upload for a survey "Document upload" question
 * (web). Unlike photos (capture-only), this accepts PDF / JPG / PNG files from
 * the device, up to `maxCount` files at `maxMb` each. Images over the limit are
 * compressed; non-image files over the limit are rejected. Uploaded files are
 * saved via `POST /documents`. The stored answer is a JSON array of {id, filename}.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { FileText, Image as ImageIcon, X, Upload, Loader2 } from 'lucide-react';
import { api, API_BASE } from '@/lib/api';
import { toast } from 'sonner';

interface Doc { id: string; filename: string; mime?: string }

const parseDocs = (v: string): Doc[] => {
  try { const a = v ? JSON.parse(v) : []; return Array.isArray(a) ? a : []; } catch { return []; }
};

const readAsDataURL = (file: File): Promise<string> => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result as string);
  r.onerror = rej;
  r.readAsDataURL(file);
});

async function compressImage(file: File, maxBytes: number): Promise<string> {
  const dataUrl = await readAsDataURL(file);
  const img = new Image();
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataUrl; });
  const maxEdge = 1920;
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  let quality = 0.85;
  let out = canvas.toDataURL('image/jpeg', quality);
  while (out.length * 0.75 > maxBytes && quality > 0.4) {
    quality -= 0.1;
    out = canvas.toDataURL('image/jpeg', quality);
  }
  return out;
}

export const DocumentUpload: React.FC<{
  instructionId?: string; token?: string; questionCode: string;
  value: string; onChange: (v: string) => void; testId: string;
  maxCount?: number; maxMb?: number;
}> = ({ instructionId, token, questionCode, value, onChange, testId, maxCount = 10, maxMb = 2 }) => {
  const docs = parseDocs(value);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const client = token ? axios.create({ baseURL: API_BASE }) : api;
  const limit = maxMb * 1024 * 1024;

  const onFiles = async (fileList: FileList | null): Promise<void> => {
    if (!fileList || !fileList.length) return;
    const files = Array.from(fileList);
    if (docs.length + files.length > maxCount) {
      toast.error(`You can upload at most ${maxCount} documents.`);
      return;
    }
    setBusy(true);
    const added: Doc[] = [];
    try {
      for (const file of files) {
        const isImage = file.type.startsWith('image/');
        let dataUrl: string;
        if (file.size > limit) {
          if (isImage) {
            dataUrl = await compressImage(file, limit);
          } else {
            toast.error(`${file.name}: too large (max ${maxMb} MB).`);
            continue;
          }
        } else {
          dataUrl = isImage ? await readAsDataURL(file) : await readAsDataURL(file);
        }
        try {
          const r = await client.post('/documents', {
            instruction_id: instructionId, tag: questionCode, filename: file.name, data_url: dataUrl,
          });
          added.push({ id: r.data.id, filename: r.data.filename, mime: r.data.mime });
        } catch (err: unknown) {
          const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
          toast.error(`${file.name}: ${detail ?? 'upload failed'}`);
        }
      }
      if (added.length) {
        onChange(JSON.stringify([...docs, ...added]));
        toast.success(`${added.length} document${added.length > 1 ? 's' : ''} uploaded`);
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (id: string): Promise<void> => {
    try { await client.delete(`/documents/${id}`); } catch { /* ignore */ }
    onChange(JSON.stringify(docs.filter((d) => d.id !== id)));
  };

  return (
    <div className="grid gap-2" data-testid={testId}>
      {docs.length > 0 && (
        <div className="grid gap-1.5">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2" data-testid={`${testId}-doc-${d.id}`}>
              {d.mime?.startsWith('image/') ? <ImageIcon size={15} className="text-raptor-blue shrink-0" /> : <FileText size={15} className="text-raptor-blue shrink-0" />}
              <span className="text-sm text-raptor-navy truncate flex-1">{d.filename}</span>
              <button type="button" onClick={() => void remove(d.id)} className="text-raptor-navy/40 hover:text-red-500" data-testid={`${testId}-remove-${d.id}`}>
                <X size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
      {docs.length < maxCount && (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-3 py-3 text-sm text-raptor-navy/70 hover:border-raptor-blue/50 hover:text-raptor-navy disabled:opacity-50"
          data-testid={`${testId}-add`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {busy ? 'Uploading…' : `Upload document (PDF, JPG, PNG · max ${maxMb}MB · up to ${maxCount})`}
        </button>
      )}
      <input ref={inputRef} type="file" accept=".pdf,image/png,image/jpeg" multiple className="hidden"
        onChange={(e) => void onFiles(e.target.files)} data-testid={`${testId}-input`} />
    </div>
  );
};

export default DocumentUpload;
